import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { encryptURL, CustomPagination, findOnArray } from 'utils/functions';
import { useNavigate } from 'react-router-dom';
import { Construction, Delete, Edit, Star } from '@mui/icons-material';
import { dropBoolean } from 'utils/drops';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { useDialog } from 'components/Modals';
import Card from 'components/Card';
import Input from 'components/Input';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import InputMask from 'components/InputMask';
import ButtonMenu from 'components/ButtonMenu';
import TableContainer from 'components/TableContainer';
import localeText from 'utils/localeText';
import styles from '../styles';
import moment from 'moment';

const Processo = ({
  drops,
  onSubmit,
  onDelete,
  loading,
  disableActions,
  onSubmitProcesso,
  documentoItem = {},
  onDeleteApontamento,
}) => {
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const rotina = 'DocumentoItemProcesso';

  const onConfirm = (values) => {
    const data = {
      Itens: [
        {
          id: documentoItem?.id,
          documento_id: documentoItem?.documento_id,
          Processos: [values],
        },
      ],
    };

    onSubmit(data);
  };

  const Modal = ({ item }) => {
    const defaultValues = {
      id: item?.id,
      documento_item_id: item?.documento_item_id,
      operacao: item?.operacao || '',
      descricao: item?.descricao || '',
      apontamento: item?.apontamento || null,
      ccusto_id: item?.ccusto_id || null,
      tpreparacao: item?.tpreparacao || '',
      texecucao: item?.texecucao || '',
      analisa_id: item?.analisa_id || null,
      aprova_id: item?.aprova_id || null,
    };
    const { control, handleSubmit } = useForm({ defaultValues });
    const { postLoading } = useContext(DocumentosContext);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" component="div" align="center">
            Informações Gerais
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputMask name="operacao" control={control} label="Operação" />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Input name="descricao" control={control} label="Descrição" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Dropdown
            name="apontamento"
            control={control}
            label="Apontamento"
            options={dropBoolean}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Dropdown
            name="ccusto_id"
            control={control}
            label="Centro de Custo"
            options={drops?.Ccusto}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask
            name="tpreparacao"
            control={control}
            label="Tempo de Preparação (Centesimal)"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask
            name="texecucao"
            control={control}
            label="Tempo de Execução (Centesimal)"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Dropdown
            name="analisa_id"
            control={control}
            label="Responsável pela Análise"
            options={drops?.Users}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Dropdown
            name="aprova_id"
            control={control}
            label="Responsável pela Aprovação"
            options={drops?.Users}
          />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(onConfirm)}
            loading={postLoading}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    );
  };

  const ApontModal = () => {
    const { deleteLoading } = useContext(DocumentosContext);
    const rows = [];
    documentoItem?.DocumentoItemProcessos?.forEach((processo) => {
      processo?.DocumentoItemProcessoAponts?.forEach((apont) => {
        rows.push({
          id: apont?.id,
          autor: findOnArray(apont?.users_id, drops?.Users, 'label'),
          executante: findOnArray(apont?.executante_id, drops?.Users, 'label'),
          inicio: apont?.inicio,
          fim: apont?.fim,
          quantidade: apont?.quantidade,
          perda: apont?.perda,
        });
      });
    });

    const getHoras = (apontamentos = []) => {
      let tempo_total = 0;
      apontamentos?.map((a) => {
        if (Boolean(a?.inicio) && Boolean(a?.fim)) {
          const dif = moment(a?.fim)?.diff(moment(a?.inicio));
          tempo_total += moment?.duration(dif)?.asMilliseconds();
        }
      });

      const duracao = moment?.duration(tempo_total);
      const dias = Math?.floor(duracao?.asDays());
      const horas = duracao?.hours();
      const minutos = duracao?.minutes();
      const segundos = duracao?.seconds();

      return `${dias}d: ${horas}h: ${minutos}m: ${segundos}s`;
    };

    const TempoCell = ({ row }) => {
      const lines = [
        {
          label: 'Início',
          value:
            moment(row?.inicio).isValid() &&
            moment(row?.inicio).format('DD/MM/YYYY [às] HH:mm'),
        },
        {
          label: 'Fim',
          value:
            moment(row?.fim).isValid() &&
            moment(row?.fim).format('DD/MM/YYYY [às] HH:mm'),
        },
        {
          label: 'Total',
          value: Boolean(row?.inicio) && Boolean(row?.fim) && getHoras([row]),
        },
      ];

      return (
        <Box flex={1}>
          {lines?.map(
            ({ label, value }, i) =>
              Boolean(value) && (
                <Box key={i?.toString()} display="flex">
                  <Typography flex={1} variant="caption" fontWeight="bold">
                    {label}:{' '}
                  </Typography>
                  <Typography flex={2} variant="caption">
                    {value}
                  </Typography>
                </Box>
              )
          )}
        </Box>
      );
    };

    const QuantidadeCell = ({ row }) => {
      const lines = [
        {
          label: 'Produzida',
          value: row?.quantidade || 0,
        },
        {
          label: 'Perda',
          value: row?.perda || 0,
        },
      ];

      return (
        <Box flex={1}>
          {lines?.map(
            ({ label, value }, i) =>
              Boolean(value) && (
                <Box key={i?.toString()} display="flex">
                  <Typography flex={1} variant="caption" fontWeight="bold">
                    {label}:{' '}
                  </Typography>
                  <Typography flex={2} variant="caption">
                    {value}
                  </Typography>
                </Box>
              )
          )}
        </Box>
      );
    };

    const onDelete = (id) => onDeleteApontamento([id]);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DataGrid
            rows={rows}
            columns={[
              {
                field: 'autor',
                headerName: 'Autor',
                flex: 1,
                minWidth: 300,
                sortable: false,
              },
              {
                field: 'executante',
                headerName: 'Executante',
                flex: 1,
                minWidth: 300,
                sortable: false,
              },
              {
                field: 'tempo',
                headerName: 'Tempo',
                flex: 1,
                minWidth: 300,
                sortable: false,
                renderCell: (props) => <TempoCell {...props} />,
              },
              {
                field: 'quantidade',
                headerName: 'Quantidade',
                flex: 1,
                minWidth: 300,
                sortable: false,
                renderCell: (props) => <QuantidadeCell {...props} />,
              },
              {
                field: 'actions',
                headerName: 'Ações',
                type: 'actions',
                width: 100,
                getActions: ({ row }) => [
                  <GridActionsCellItem
                    size="small"
                    icon={<Delete />}
                    label="Excluir"
                    onClick={() => onDelete(row?.id)}
                  />,
                ],
              },
            ]}
            loading={deleteLoading}
            pagination
            autoHeight
            pageSize={5}
            density="compact"
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            localeText={localeText}
            disableSelectionOnClick
            getRowHeight={() => 'auto'}
            components={{
              LoadingOverlay: LinearProgress,
              Pagination: CustomPagination,
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const options = [
    {
      name: 'Adicionar Processo',
      icon: 'add',
      action: () => openDialog(<Modal />, 'Adicionar Processo'),
    },
    {
      name: 'Recarregar Processo',
      icon: 'refresh',
      action: () => onSubmitProcesso({ id: documentoItem?.id }),
    },
  ];

  return (
    <Card title="Processos" style={styles?.card}>
      <Grid container spacing={2}>
        {!disableActions && (
          <Grid item xs={12} textAlign="center">
            <ButtonMenu
              buttonProps={{ variant: 'outlined', loading }}
              options={options}
            />
          </Grid>
        )}
        {!!documentoItem?.DocumentoItemProcessos?.length && (
          <Grid item xs={12}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Operação</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Centro de Custo</TableCell>
                    <TableCell>Tempo de Preparo</TableCell>
                    <TableCell>Tempo de Execução</TableCell>
                    <TableCell>Apontamento</TableCell>
                    <TableCell>Responsável pela Análise</TableCell>
                    <TableCell>Responsável pela Aprovação</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentoItem?.DocumentoItemProcessos?.map((item) => (
                    <TableRow key={item?.id?.toString()}>
                      <TableCell>{item?.operacao}</TableCell>
                      <TableCell>{item?.descricao}</TableCell>
                      <TableCell>{item?.Ccusto?.descricao}</TableCell>
                      <TableCell>{item?.tpreparacao}</TableCell>
                      <TableCell>{item?.texecucao}</TableCell>
                      <TableCell>{item?.apontamento}</TableCell>
                      <TableCell>{item?.analisa}</TableCell>
                      <TableCell>{item?.aprova}</TableCell>
                      <TableCell align="center">
                        {!disableActions && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              openDialog(
                                <Modal item={item} />,
                                'Editar Processo'
                              )
                            }
                          >
                            <Edit />
                          </IconButton>
                        )}
                        {!disableActions && (
                          <IconButton
                            size="small"
                            onClick={() => onDelete({ rotina, id: item?.id })}
                          >
                            <Delete />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/app/Documento/Qualidade/${encryptURL({ processo_id: item?.id })}`
                            )
                          }
                        >
                          <Star />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openDialog(<ApontModal />, 'Apontamentos')
                          }
                        >
                          <Construction />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default Processo;
